import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  HashRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import {
  api,
  ApiError,
  type Expense,
  type NewExpense,
  type User,
  type UserPreferences,
} from "./api";
import { useViewport } from "./hooks";

type UserPatch = Parameters<typeof api.updateMe>[0];
import { LoginScreen } from "./screens/Login";
import { SignupScreen } from "./screens/Signup";
import { HomeScreen } from "./screens/Home";
import { AddExpenseModal } from "./screens/AddExpense";
import { SettingsScreen } from "./screens/Settings";
import { PrivacyPolicyScreen } from "./screens/PrivacyPolicy";
import { TermsOfServiceScreen } from "./screens/TermsOfService";

type AppContextValue = {
  user: User | null;
  expenses: Expense[];
  isDesktop: boolean;
  addExpense: (e: NewExpense) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  updateUser: (patch: UserPatch, opts?: { silent?: boolean }) => Promise<void>;
  setPreference: (key: keyof UserPreferences, value: boolean) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  openAddExpense: () => void;
  handleSignedIn: (u: User) => Promise<void>;
  handleSignedUp: (u: User) => Promise<void>;
};

const AppCtx = createContext<AppContextValue | null>(null);

function useApp() {
  const v = useContext(AppCtx);
  if (!v)
    throw new Error(
      "AppCtx missing — only available inside AuthenticatedShell",
    );
  return v;
}

export function BudgetApp() {
  const { isDesktop } = useViewport();

  return (
    <HashRouter>
      <Routes>
        {/* Standalone legal pages — no auth check, no /me call */}
        <Route
          path="/privacy"
          element={<PrivacyRoute isDesktop={isDesktop} />}
        />
        <Route path="/terms" element={<TermsRoute isDesktop={isDesktop} />} />

        {/* Everything else lives behind the auth shell */}
        <Route element={<AuthenticatedShell isDesktop={isDesktop} />}>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <LoginRoute />
              </PublicOnly>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicOnly>
                <SignupRoute />
              </PublicOnly>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <HomeRoute />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsRoute />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}

function AuthenticatedShell({ isDesktop }: { isDesktop: boolean }) {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const autoOpenedRef = useRef(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  const loadExpenses = useCallback(async () => {
    try {
      const list = await api.listExpenses();
      setExpenses(list);
    } catch {
      setExpenses([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await api.me();
        setUser(u);
        await loadExpenses();
      } catch {
        /* not logged in */
      }
      setLoading(false);
    })();
  }, [loadExpenses]);

  useEffect(() => {
    if (loading || !user || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    if (user.preferences.autoOpenAdd) setAddOpen(true);
  }, [user, loading]);

  const handleSignedIn = useCallback(
    async (signedIn: User) => {
      setUser(signedIn);
      await loadExpenses();
    },
    [loadExpenses],
  );

  const handleSignedUp = useCallback(async (created: User) => {
    setUser(created);
    setExpenses([]);
    setTimeout(() => setAddOpen(true), 350);
  }, []);

  const addExpense = useCallback(
    async (exp: NewExpense) => {
      try {
        const created = await api.addExpense(exp);
        setExpenses((prev) => [created, ...prev]);
        setAddOpen(false);
        showToast(t("toasts.expenseAdded"));
      } catch (e) {
        showToast(e instanceof Error ? e.message : t("errors.failedToAdd"));
      }
    },
    [showToast, t],
  );

  const deleteExpense = useCallback(
    async (id: string) => {
      setExpenses((prev) => {
        const next = prev.filter((e) => e.id !== id);
        api.deleteExpense(id).catch(() => {
          setExpenses(prev);
          setToast(t("errors.deleteFailed"));
          setTimeout(() => setToast(null), 2000);
        });
        return next;
      });
    },
    [t],
  );

  const updateUser = useCallback(
    async (patch: UserPatch, opts?: { silent?: boolean }) => {
      try {
        const updated = await api.updateMe(patch);
        setUser(updated);
        if (!opts?.silent) showToast(t("toasts.saved"));
      } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new Error(t("errors.saveFailed"));
      }
    },
    [showToast, t],
  );

  const setPreference = useCallback(
    async (key: keyof UserPreferences, value: boolean) => {
      await updateUser({ preferences: { [key]: value } }, { silent: true });
    },
    [updateUser],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore — proceed with local clear */
    }
    setUser(null);
    setExpenses([]);
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await api.deleteMe();
    } catch {
      /* ignore — proceed with local reset */
    }
    setUser(null);
    setExpenses([]);
  }, []);

  if (loading) {
    return (
      <div className="app-shell">
        <LoadingState />
      </div>
    );
  }

  const ctx: AppContextValue = {
    user,
    expenses,
    isDesktop,
    addExpense,
    deleteExpense,
    updateUser,
    setPreference,
    logout,
    deleteAccount,
    openAddExpense: () => setAddOpen(true),
    handleSignedIn,
    handleSignedUp,
  };

  return (
    <AppCtx.Provider value={ctx}>
      <div className="app-shell">
        <Outlet />

        {addOpen && user && (
          <AddExpenseModal
            username={user.username}
            categories={user.categories ?? []}
            quickAdds={user.quickAdds ?? []}
            isDesktop={isDesktop}
            onClose={() => setAddOpen(false)}
            onAdd={addExpense}
          />
        )}

        {toast && (
          <div
            role="status"
            style={{
              position: "fixed",
              bottom: 50,
              left: "50%",
              transform: "translateX(-50%)",
              background: "var(--ink)",
              color: "white",
              padding: "10px 18px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--accent)",
              }}
            />
            {toast}
          </div>
        )}
      </div>
    </AppCtx.Provider>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useApp();
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user } = useApp();
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function LoginRoute() {
  const { isDesktop, handleSignedIn } = useApp();
  const navigate = useNavigate();
  return (
    <LoginScreen
      isDesktop={isDesktop}
      onSignedIn={async (u) => {
        await handleSignedIn(u);
        navigate("/", { replace: true });
      }}
      onSwitchToSignup={() => navigate("/signup")}
      onOpenPrivacy={() => navigate("/privacy")}
      onOpenTerms={() => navigate("/terms")}
    />
  );
}

function SignupRoute() {
  const { isDesktop, handleSignedUp } = useApp();
  const navigate = useNavigate();
  return (
    <SignupScreen
      isDesktop={isDesktop}
      onSignedUp={async (u) => {
        await handleSignedUp(u);
        navigate("/", { replace: true });
      }}
      onSwitchToLogin={() => navigate("/login")}
      onOpenPrivacy={() => navigate("/privacy")}
      onOpenTerms={() => navigate("/terms")}
    />
  );
}

function HomeRoute() {
  const { user, expenses, isDesktop, deleteExpense, openAddExpense } = useApp();
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <HomeScreen
      user={user}
      expenses={expenses}
      isDesktop={isDesktop}
      onOpenAdd={openAddExpense}
      onOpenSettings={() => navigate("/settings")}
      onDeleteExpense={deleteExpense}
    />
  );
}

function SettingsRoute() {
  const { user, isDesktop, updateUser, setPreference, logout, deleteAccount } =
    useApp();
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <SettingsScreen
      user={user}
      isDesktop={isDesktop}
      onUpdate={updateUser}
      onSetPreference={setPreference}
      onBack={() => navigate("/")}
      onDeleteAccount={async () => {
        await deleteAccount();
        navigate("/signup", { replace: true });
      }}
      onLogout={async () => {
        await logout();
        navigate("/login", { replace: true });
      }}
      onOpenPrivacy={() => navigate("/privacy")}
      onOpenTerms={() => navigate("/terms")}
    />
  );
}

function PrivacyRoute({ isDesktop }: { isDesktop: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="app-shell">
      <PrivacyPolicyScreen
        isDesktop={isDesktop}
        onBack={() => goBack(navigate)}
      />
    </div>
  );
}

function TermsRoute({ isDesktop }: { isDesktop: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="app-shell">
      <TermsOfServiceScreen
        isDesktop={isDesktop}
        onBack={() => goBack(navigate)}
      />
    </div>
  );
}

function goBack(navigate: ReturnType<typeof useNavigate>) {
  if (window.history.length > 1) navigate(-1);
  else navigate("/");
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--muted)",
        fontSize: 13,
        letterSpacing: ".08em",
        textTransform: "uppercase",
      }}
    >
      {t("common.loading")}
    </div>
  );
}
